// ═══════════════════════════════════════════════════════════════════════════
// Ver el documento, y desde ahí guardarlo
//
// El operador: "es mucho más cómodo que cada remisión, factura, remisión de
// envío se vea en modo preview en lugar de que se descargue, luego haya que
// buscarla y abrirla. Mejor que del preview se pueda descargar."
//
// El costo del camino viejo se notaba sobre todo cuando algo salía mal: para
// descubrir que una remisión tenía el cliente equivocado había que descargarla,
// buscarla en la carpeta de Descargas, abrirla, cerrarla y borrarla. Cuatro
// pasos para mirar, y una carpeta llena de PDF que nadie quería.
//
// ── POR QUÉ ESTÁ AQUÍ Y NO EN CADA PANTALLA ────────────────────────────────
//
// Se engancha al ÚNICO sitio por el que salen todos los documentos
// (`pdf/guardar.js`). Así vale para la remisión, la de envío, la lista de
// precios, el CFDI y el complemento sin tocar ninguno de sus generadores — y
// lo que se agregue mañana lo hereda sin hacer nada.
//
// En iOS resuelve además un problema viejo: la pestaña de la app nunca navega
// a una URL `blob:`, así que desaparece el "WebKitBlobResource error 1" al
// recargar que se documentó en `guardar.js`.
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from "react";
import { instalarVisor, canCompartirArchivo, compartirArchivo, compartirArchivos,
         esIOS } from "../pdf/guardar.js";
import { btnPrimary, btnSecondary } from "./estilos.js";

export function VisorDocumento() {
  const [doc, setDoc] = useState(null);

  useEffect(() => {
    instalarVisor((d) => setDoc(d));
    return () => instalarVisor(null);
  }, []);

  // Escape cierra. Es un visor, no un formulario: salir tiene que ser barato.
  useEffect(() => {
    if (!doc) return;
    const h = (e) => { if (e.key === "Escape") cerrar(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  });

  const cerrar = () => {
    setDoc(d => { try { d?.alCerrar?.(); } catch { /* ya liberado */ } return null; });
  };

  if (!doc) return null;

  // ── Compartir o descargar, según lo que el aparato sepa hacer ───────────
  //
  // En iPhone "descargar" no existe: `<a download>` se ignora y el PDF solo se
  // despliega. Lo que sí hay es la hoja nativa, y desde ella salen WhatsApp,
  // Correo y «Guardar en Archivos» — que era exactamente lo que se pedía.
  //
  // Se pregunta al navegador en vez de mirar el userAgent: un Android moderno
  // también comparte, y un iPad con teclado no deja de ser iPad. El botón que
  // no se puede cumplir no se enseña.
  // ── Con adjuntos: el XML de un CFDI ─────────────────────────────────────
  //
  // El operador: "No hay botón para descargar el xml y como no tiene visor al
  // ofrecer compartir da el url. Debería tener un botón para descargar
  // pdf+xml."
  //
  // Los botones hablan de TODOS los archivos. Se comparte si la hoja acepta
  // cada uno —en Android, Chrome no acepta .xml— y se descarga donde
  // descargar funciona, que es todo menos iOS: ahí `<a download>` no existe y
  // «Guardar en Archivos» vive dentro de la hoja.
  const adjuntos = doc.adjuntos || [];
  const todos = [{ blob: doc.blob, nombre: doc.nombre }, ...adjuntos];
  const etiquetaTodos = todos.map(a => (a.nombre.split(".").pop() || "").toUpperCase()).join("+");
  const puedeCompartir = !!doc.blob && todos.every(a => canCompartirArchivo(a.nombre));
  const puedeDescargar = !puedeCompartir || !esIOS();

  const compartir = async () => {
    const ok = adjuntos.length
      ? await compartirArchivos(todos, doc.titulo || doc.nombre)
      : await compartirArchivo(doc.blob, doc.nombre);
    if (!ok) descargar();            // si la hoja no abre, al menos lo de antes
  };
  const descargar = () => (adjuntos.length ? doc.descargarTodos?.() : doc.descargar?.());

  return (
    <div onClick={cerrar}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.9)", zIndex: 2000,
               display: "flex", flexDirection: "column", padding: 12, gap: 10,
               // En iOS la barra de Safari entra y sale, y `inset:0` mide el
               // viewport grande: sin esto el pie del visor queda debajo del
               // borde y hay que hacer pinch para alcanzarlo. `dvh` sigue a la
               // barra; el `height` de antes queda de respaldo para quien no lo
               // entienda.
               height: "100%", maxHeight: "100dvh",
               paddingBottom: "max(12px, env(safe-area-inset-bottom))" }}>

      {/* Los botones ANTES envolvían pero se apretaban contra el nombre del
          archivo, que pedía 160px de mínimo. En un iPhone de 375 no cabían los
          tres y el último se salía. Ahora el nombre ocupa su propio renglón
          cuando hace falta y los botones nunca se parten. */}
      <div onClick={e => e.stopPropagation()}
        style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
                 color: "#e8e0d4" }}>
        <span style={{ fontSize: 13, flexBasis: "100%", overflow: "hidden",
                       textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {doc.nombre}
        </span>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", width: "100%" }}>
          {puedeCompartir && (
            <button onClick={compartir}
              style={{ ...btnPrimary, padding: "10px 16px", fontSize: 13, flex: "1 1 auto" }}>
              Compartir{adjuntos.length ? ` ${etiquetaTodos}` : ""}
            </button>
          )}
          {puedeDescargar && (
            <button onClick={descargar}
              style={{ ...(puedeCompartir ? btnSecondary : btnPrimary),
                       padding: "10px 16px", fontSize: 13, flex: "1 1 auto" }}>
              Descargar{adjuntos.length ? ` ${etiquetaTodos}` : ""}
            </button>
          )}
          <a href={doc.url} target="_blank" rel="noreferrer"
            style={{ ...btnSecondary, padding: "10px 14px", fontSize: 13, textDecoration: "none",
                     textAlign: "center", flex: "1 1 auto" }}>
            Abrir aparte
          </a>
          <button onClick={cerrar}
            style={{ ...btnSecondary, padding: "10px 14px", fontSize: 13, flex: "0 1 auto" }}>
            Cerrar
          </button>
        </div>
      </div>

      <div onClick={e => e.stopPropagation()}
        style={{ flex: 1, background: "#fff", borderRadius: 8, overflow: "hidden",
                 minHeight: 0 }}>
        <iframe src={doc.url} title={doc.nombre}
          style={{ width: "100%", height: "100%", border: "none" }} />
      </div>

      <div onClick={e => e.stopPropagation()}
        style={{ fontSize: 10, color: "#666", textAlign: "center" }}>
        {puedeCompartir
          ? `«Compartir» abre WhatsApp, Correo o Guardar en Archivos${adjuntos.length ? ` — van los ${todos.length} archivos juntos` : ""}`
          : "Escape para cerrar · «Abrir aparte» si prefieres verlo en otra pestaña"}
      </div>
    </div>
  );
}
